/**
 * Frelan: per-variant hover image on product cards (snippets/card-variant-hover-image.liquid).
 *
 * On mouse hover over the card gallery, show the first `additional_gallery_images` image of
 * the variant the card currently shows, cross-fading over the card image. The variant is read
 * from the card link's `?variant=` (Horizon's product-card.js keeps that link in step when a
 * finish is picked on the card), so it always matches the image on screen.
 *
 * The image is only requested on first hover, and only revealed once decoded, so there is no
 * half-loaded flash; a quick pass over the card that leaves before it loads shows nothing.
 * Deliberately standalone: it does not touch product-card.js or the card slideshow.
 */
class CardHoverImage extends HTMLElement {
  /** @type {Record<string, { src: string, srcset: string, alt: string }>} */
  #images = {};

  /** @type {HTMLElement | null} */
  #gallery = null;

  /** True while the pointer is over the gallery; guards a late decode after leaving. */
  #hovering = false;

  connectedCallback() {
    try {
      this.#images = JSON.parse(this.querySelector('script[type="application/json"]')?.textContent || '{}');
    } catch {
      this.#images = {};
    }

    this.#gallery = this.closest('.card-gallery');
    this.#gallery?.addEventListener('pointerenter', this.#onEnter);
    this.#gallery?.addEventListener('pointerleave', this.#onLeave);
  }

  disconnectedCallback() {
    this.#gallery?.removeEventListener('pointerenter', this.#onEnter);
    this.#gallery?.removeEventListener('pointerleave', this.#onLeave);
  }

  /** @returns {HTMLImageElement | null} */
  get #img() {
    return this.querySelector('.card-hover-image__img');
  }

  /** The variant the card currently links to. */
  #currentVariantId() {
    const link = this.closest('product-card')?.querySelector('a.product-card__link');
    const href = link instanceof HTMLAnchorElement ? link.href : '';
    if (!href) return null;
    try {
      return new URL(href, window.location.origin).searchParams.get('variant');
    } catch {
      return null;
    }
  }

  /** @param {PointerEvent} event */
  #onEnter = async (event) => {
    if (event.pointerType !== 'mouse') return;
    if (this.closest('product-card')?.hasAttribute('data-finishes-open')) return;

    const variantId = this.#currentVariantId();
    const entry = variantId ? this.#images[variantId] : undefined;
    const img = this.#img;
    if (!entry || !img) return;

    this.#hovering = true;

    if (img.dataset.variantId !== variantId) {
      this.removeAttribute('data-visible');
      img.dataset.variantId = variantId;
      img.sizes = this.dataset.sizes || '50vw';
      if (entry.srcset) img.srcset = entry.srcset;
      img.src = entry.src;
      img.alt = entry.alt || '';
    }

    try {
      await img.decode();
    } catch {
      return; // broken image: keep the card image
    }

    // Still hovering, and still the same variant (a finish change may have happened meanwhile).
    if (this.#hovering && img.dataset.variantId === this.#currentVariantId()) {
      this.setAttribute('data-visible', '');
    }
  };

  /** @param {PointerEvent} event */
  #onLeave = (event) => {
    if (event.pointerType !== 'mouse') return;
    this.#hovering = false;
    this.removeAttribute('data-visible');
  };
}

if (!customElements.get('card-hover-image')) {
  customElements.define('card-hover-image', CardHoverImage);
}
